import { cn } from "@/lib/utils";

export type MascotMood =
  | "normal"
  | "happy"
  | "worried"
  | "sleepy"
  | "celebrate"
  | "crying"; // 資金ショート（残高マイナス）— 泣きながら倒れる

interface ZaimuKunProps {
  mood?: MascotMood;
  size?: number;
  className?: string;
}

/**
 * ザイムくん — クマ立ち姿（赤ちゃん体型・マスター）
 * 頭デカ + 短足2本 + 耳ピョコ + 縦長シルエット
 *
 * ## 使い方
 * このファイルをプロジェクトの `src/components/mascot/zaimu-kun.tsx` 等にコピー。
 * `cn()` ユーティリティ（clsx + tailwind-merge）が必要。
 *
 * ## マスター保管場所
 *   ~/Documents/Claude/00_分析基礎情報_共通/mascot/
 *
 * ## ライセンス
 * 上田純市郎個人プライベート利用専用。
 */
export function ZaimuKun({
  mood = "normal",
  size = 80,
  className,
}: ZaimuKunProps) {
  // Claude（Anthropic）ブランドカラーに合わせた暖かみのあるオレンジ
  const BODY = "#D97757";        // Claude メインオレンジ
  const BODY_SHADE = "#B85839";  // 影色（やや濃いオレンジブラウン）
  const EYE = "#1A1A1A";
  const HL = "#FFFFFF";

  // 泣いて倒れている時は横倒しレイアウト（縦長 → 横長）
  if (mood === "crying") {
    const TEAR = "#3B82F6";
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        shapeRendering="crispEdges"
        xmlns="http://www.w3.org/2000/svg"
        className={cn(className)}
        aria-label={`ザイムくん（${mood}）— 資金ショート予測`}
        role="img"
      >
        {/* 床のライン（破線風） */}
        <rect x="0" y="22" width="2" height="1" fill="#94A3B8" />
        <rect x="3" y="22" width="2" height="1" fill="#94A3B8" />
        <rect x="6" y="22" width="2" height="1" fill="#94A3B8" />
        <rect x="9" y="22" width="2" height="1" fill="#94A3B8" />
        <rect x="12" y="22" width="2" height="1" fill="#94A3B8" />
        <rect x="15" y="22" width="2" height="1" fill="#94A3B8" />
        <rect x="18" y="22" width="2" height="1" fill="#94A3B8" />
        <rect x="21" y="22" width="2" height="1" fill="#94A3B8" />

        {/* 横倒し: 体（左側）*/}
        <rect x="2" y="14" width="6" height="6" fill={BODY} />
        <rect x="2" y="20" width="6" height="1" fill={BODY_SHADE} />
        {/* 短足（横向きに飛び出す） */}
        <rect x="3" y="13" width="2" height="1" fill={BODY_SHADE} />
        <rect x="6" y="13" width="2" height="1" fill={BODY_SHADE} />

        {/* 横倒し: 頭部（右側）*/}
        <rect x="9" y="14" width="1" height="6" fill={BODY} />
        <rect x="10" y="13" width="1" height="8" fill={BODY} />
        <rect x="11" y="12" width="7" height="9" fill={BODY} />
        <rect x="18" y="13" width="1" height="7" fill={BODY} />
        {/* 耳（横向き） */}
        <rect x="13" y="11" width="2" height="1" fill={BODY} />
        <rect x="13" y="9" width="2" height="1" fill={BODY} />

        {/* 閉じた目（横向き「>」） */}
        <rect x="13" y="14" width="1" height="1" fill={EYE} />
        <rect x="14" y="15" width="1" height="1" fill={EYE} />
        <rect x="13" y="16" width="1" height="1" fill={EYE} />
        <rect x="13" y="18" width="1" height="1" fill={EYE} />
        <rect x="14" y="19" width="1" height="1" fill={EYE} />
        <rect x="13" y="20" width="1" height="1" fill={EYE} />

        {/* 涙のしずく（目から床に向かって） */}
        <rect x="15" y="15" width="1" height="1" fill={TEAR} />
        <rect x="15" y="17" width="1" height="2" fill={TEAR} />
        <rect x="16" y="20" width="1" height="2" fill={TEAR} />
      </svg>
    );
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      shapeRendering="crispEdges"
      xmlns="http://www.w3.org/2000/svg"
      className={cn(className)}
      aria-label={`ザイムくん（${mood}）`}
      role="img"
    >
      {/* 耳 */}
      <rect x="6" y="2" width="2" height="2" fill={BODY} />
      <rect x="16" y="2" width="2" height="2" fill={BODY} />

      {/* 頭部 */}
      <rect x="6" y="4" width="12" height="1" fill={BODY} />
      <rect x="4" y="5" width="16" height="1" fill={BODY} />
      <rect x="3" y="6" width="18" height="7" fill={BODY} />
      <rect x="4" y="13" width="16" height="1" fill={BODY} />

      {/* 体 */}
      <rect x="6" y="14" width="12" height="6" fill={BODY} />
      <rect x="7" y="20" width="10" height="1" fill={BODY_SHADE} />

      {/* 短足2本 */}
      <rect x="8" y="21" width="2" height="2" fill={BODY_SHADE} />
      <rect x="14" y="21" width="2" height="2" fill={BODY_SHADE} />

      {/* 表情（目だけ・口なし） */}
      {mood === "normal" && (
        <>
          <rect x="9" y="9" width="2" height="2" fill={EYE} />
          <rect x="13" y="9" width="2" height="2" fill={EYE} />
          <rect x="9" y="9" width="1" height="1" fill={HL} />
          <rect x="13" y="9" width="1" height="1" fill={HL} />
        </>
      )}
      {mood === "happy" && (
        <>
          <rect x="8" y="10" width="1" height="1" fill={EYE} />
          <rect x="9" y="9" width="1" height="1" fill={EYE} />
          <rect x="10" y="10" width="1" height="1" fill={EYE} />
          <rect x="13" y="10" width="1" height="1" fill={EYE} />
          <rect x="14" y="9" width="1" height="1" fill={EYE} />
          <rect x="15" y="10" width="1" height="1" fill={EYE} />
        </>
      )}
      {mood === "worried" && (
        <>
          <rect x="9" y="10" width="2" height="2" fill={EYE} />
          <rect x="13" y="10" width="2" height="2" fill={EYE} />
        </>
      )}
      {mood === "sleepy" && (
        <>
          <rect x="8" y="10" width="3" height="1" fill={EYE} />
          <rect x="13" y="10" width="3" height="1" fill={EYE} />
        </>
      )}
      {mood === "celebrate" && (
        <>
          <rect x="9" y="8" width="1" height="1" fill={EYE} />
          <rect x="8" y="9" width="3" height="1" fill={EYE} />
          <rect x="9" y="10" width="1" height="1" fill={EYE} />
          <rect x="14" y="8" width="1" height="1" fill={EYE} />
          <rect x="13" y="9" width="3" height="1" fill={EYE} />
          <rect x="14" y="10" width="1" height="1" fill={EYE} />
        </>
      )}
    </svg>
  );
}

export function getZaimuKunMessage(
  mood: MascotMood,
  context?: { budgetRate?: number; daysSinceLastInput?: number; minBalance?: number }
): string {
  switch (mood) {
    case "happy":
      return "今日もいい調子！";
    case "celebrate":
      return "やったね！";
    case "worried":
      if (context?.minBalance !== undefined) {
        return `余裕は ${context.minBalance.toLocaleString()}円。要注意`;
      }
      return "ちょっと注意かも";
    case "crying":
      if (context?.minBalance !== undefined) {
        return `資金ショート予測 ${context.minBalance.toLocaleString()}円…`;
      }
      return "資金ショート予測…";
    case "sleepy":
      if (context?.daysSinceLastInput !== undefined) {
        return `${context.daysSinceLastInput}日入力なし`;
      }
      return "ひさしぶり！";
    case "normal":
    default:
      return "今日はどうだった？";
  }
}
